import React, { useState, useEffect, useRef, useCallback } from 'react'
import Modal from 'react-modal'
import ReactPlayer from 'react-player'
import PropTypes from 'prop-types'
import videoPlayButton from '../img/video-play-button.svg'

const formatTime = (seconds) => {
  const date = new Date(seconds * 1000)
  const hh = date.getUTCHours()
  const mm = date.getUTCMinutes()
  const ss = pad(date.getUTCSeconds())
  if (hh) {
    return `${hh}:${pad(mm)}:${ss}`
  }
  return `${mm}:${ss}`
}

function pad(string) {
  return ('0' + string).slice(-2)
}

const VideoLightbox = ({ videolink, ctaText }) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [playing, setPlaying] = useState(false)
  const [playedTime, setPlayedTime] = useState(0)
  const [playedSeconds, setPlayedSeconds] = useState(0)
  const [duration, setDuration] = useState(0)
  const playerRef = useRef(null)

  useEffect(() => {
    const appRoot = document.body
    Modal.setAppElement(appRoot)
  }, [])

  const openModal = (event) => {
    event.preventDefault()
    setIsModalOpen(true)
  }

  const closeModal = useCallback(() => {
    //reset all on modal close
    setPlaying(false)
    setPlayedTime(0)
    setPlayedSeconds(0)
    setDuration(0)
    setIsModalOpen(false)
  }, [])

  const playPauseHandler = (event) => {
    // Check if the click event originated from the progress bar area (played-time or played-time-background)
    if (
      event.target.classList.contains('played-time') ||
      event.target.classList.contains('played-time-background')
    ) {
      return // If so, do nothing (exclude play/pause)
    }

    setPlaying(!playing)
  }
  const onProgressHandler = useCallback((state) => {
    setPlayedTime(state.played)
    setPlayedSeconds(formatTime(state.playedSeconds))
  }, [])

  const onDurationHandler = useCallback((duration) => {
    setDuration(formatTime(duration))
  }, [])

  const jumpHandler = useCallback((event) => {
    if (event.target.classList.contains('played-time-background')) {
      const progressBarWidth = event.currentTarget.clientWidth
      const clickedPositionX = event.nativeEvent.offsetX
      const progressPercentage = clickedPositionX / progressBarWidth

      setPlayedTime(progressPercentage)

      if (playerRef.current) {
        const duration = playerRef.current.getDuration()
        const seekTime = progressPercentage * duration
        playerRef.current.seekTo(seekTime)

        setPlaying(true)
      }
    }
  }, [])

  return (
    <div>
      <a
        className="is-uppercase has-text-black is-family-secondary is-flex is-flex-direction-row is-align-items-center"
        href={videolink}
        target="_blank"
        rel="noopener noreferrer"
        onClick={openModal}
      >
        <img
          src="/img/play-symbol.png"
          alt="Play Button"
          width={40}
          height={40}
          style={{ cursor: 'pointer' }}
        />
        <span className="ml-2">{ctaText ? ctaText : `Se video`}</span>
      </a>

      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        className="lightbox"
        overlayClassName="lightbox-overlay"
        contentLabel="Video Lightbox"
      >
        <button className="close" onClick={closeModal}>
          &times;
        </button>
        <div className="video-wrapper">
          <ReactPlayer
            url={`${videolink}#t=0.04`}
            playing={playing}
            controls={false}
            width="100%"
            height="100%"
            onProgress={onProgressHandler}
            onDuration={onDurationHandler}
            ref={playerRef}
          />
          <Control
            onPlayPause={playPauseHandler}
            playing={playing}
            playedTime={playedTime}
            playedSeconds={playedSeconds}
            duration={duration}
            onJump={jumpHandler}
          />
        </div>
      </Modal>
    </div>
  )
}

VideoLightbox.propTypes = {
  videolink: PropTypes.string.isRequired,
}

const Control = ({
  onPlayPause,
  playing,
  playedTime,
  playedSeconds,
  onJump,
  duration,
}) => {
  // Add onJump here
  return (
    <div
      className="control"
      onClick={onPlayPause}
      role="button"
      aria-label="Play/Pause"
      tabIndex="0"
      onKeyDown={(e) => {
        if (e.key === 'Enter') onPlayPause(e)
      }}
    >
      {playing ? (
        <></>
      ) : (
        <div className="play-btn">
          <img src={videoPlayButton} alt="Video Play Button" />
        </div>
      )}

      <div
        className="played-time-background"
        role="slider"
        aria-valuemin="0"
        aria-valuemax="100"
        aria-valuenow={playedTime * 100}
        onClick={onJump}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') onJump(e)
        }}
        tabIndex="0"
      >
        <div className="played-time-number">
          {playedSeconds}/{duration}
        </div>
        <div
          className="played-time"
          style={{ transform: `scaleX(${playedTime})` }}
        ></div>
      </div>
    </div>
  )
}

Control.propTypes = {
  onPlayPause: PropTypes.func.isRequired,
  playing: PropTypes.bool.isRequired,
  playedTime: PropTypes.number.isRequired,
  playedSeconds: PropTypes.number.isRequired,
  onJump: PropTypes.func.isRequired,
}

export default VideoLightbox
